import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { push } from 'connected-react-router'
import queryString from 'query-string'
import { ApiMiddlewarePayload, ApiResponse, ErrorResponse } from '@ocean/api'
import { SessionUtils, LoggerUtils, DateUtils } from '@ocean/utils'
import { LoginType } from 'types'
import { AppThunk } from 'store'
import { createApiAction } from 'api'
import { UserUtils, EnvironmentUtils, SsoUtils } from 'utils'
import { userLoggedOut, logoutUser } from 'core/session/slices/session'
import { redirectToSta, redirectToStaWithBackUrl } from 'core/staIntegration/slices/staIntegration'
import { getUserDeclaration } from 'core/userDeclaration/slices/userDeclaration'
import {
  login,
  forgotPassword,
  setupPassword,
  changePassword,
  getAccount,
  registerInstallation,
} from 'modules/Auth/api/authService'
import {
  AuthUserParams,
  ForgotPasswordParams,
  SetupPasswordParams,
  GetAccountResponse,
  SetupPasswordEmailLinkParams,
  ChangePasswordParams,
  AccountTypes,
  LoginQueryParams,
  RegisterParams,
} from 'modules/Auth/types'
import homeRoutes from 'modules/Home/routing/routes'
import simulatorTrainingInstructorILSimulatorListRoutes from 'modules/simulatorTrainings/instructor/instructorLed/SimulatorList/routing/routes'
import { AuthState, ApiAuthentificationResponse } from './types'

const initialState: AuthState = {
  session: {},
  userAccount: undefined,
  forgotPasswordRequest: {},
  setupPasswordRequest: {},
  changePasswordRequest: {},
  registrationRequest: {},
  isOceanEnabled: false,
  shouldUseLegacyClient: false,
  externalLoginError: undefined,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    userAuthenticated(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiAuthentificationResponse>>
    ) {
      state.session = action.payload
    },
    userForgotPasswordRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.forgotPasswordRequest = action.payload
    },
    userSetupPasswordRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.setupPasswordRequest = action.payload
    },
    userChangePasswordRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.changePasswordRequest = action.payload
    },
    registrationRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.registrationRequest = action.payload
    },
    getAccountRequested(state, action: PayloadAction<GetAccountResponse>) {
      state.userAccount = action.payload
    },
    setIsOceanEnabled(state, action) {
      state.isOceanEnabled = action.payload
    },
    setShouldUseLegacyClient(state, action) {
      state.shouldUseLegacyClient = action.payload
    },
    setExternalLoginError(state, action: PayloadAction<ErrorResponse | undefined>) {
      state.externalLoginError = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  userAuthenticated,
  userForgotPasswordRequested,
  userSetupPasswordRequested,
  userChangePasswordRequested,
  registrationRequested,
  getAccountRequested,
  setIsOceanEnabled,
  setShouldUseLegacyClient,
  setExternalLoginError,
} = authSlice.actions

export {
  userAuthenticated,
  userForgotPasswordRequested,
  userSetupPasswordRequested,
  userChangePasswordRequested,
  registrationRequested,
  getAccountRequested,
  setExternalLoginError,
}
export default authSlice.reducer

const handleUserAuthentification =
  (response: ApiMiddlewarePayload<ApiAuthentificationResponse>): AppThunk =>
  async dispatch => {
    const { error, result } = response

    dispatch(setExternalLoginError(initialState.externalLoginError))

    if (!error && result?.login) {
      const { login } = result
      EnvironmentUtils.storeEnvironmentConfiguration(login.configuration)
      DateUtils.setCustomerDateTimeFormat(login.configuration.customerDateTimeFormat)
      SessionUtils.storeSession(login.sessionToken)
      dispatch(setIsOceanEnabled(login.isOceanEnabled))
      dispatch(setShouldUseLegacyClient(login.shouldUseLegacyClient))

      SsoUtils.unmarkSso() //* https://oceantg.atlassian.net/browse/OLP-3467

      const account: ApiResponse<GetAccountResponse> = await getAccount()
      const { success, content } = account

      if (success && content) {
        let redirectRoute: string | undefined

        // Checking for legacy params from STA - backUrl means redirect to STA
        const queryParams: LoginQueryParams = queryString.parse(window.location.search)
        const { backUrl, returnUrl } = queryParams

        if (backUrl) {
          dispatch(redirectToStaWithBackUrl(backUrl))
          return
        }

        UserUtils.storeUserAccountInfo(content)
        dispatch(getAccountRequested(content))

        // Checking special user and redirect to STA
        if (content.type === AccountTypes.SpecialUser) {
          LoggerUtils.info('Logged in as SpecialUser')
          const { simulatorDashboardForAdministratorsAvailable } =
            EnvironmentUtils.getPermissions('simulatorTraining')
          if (simulatorDashboardForAdministratorsAvailable) {
            redirectRoute = simulatorTrainingInstructorILSimulatorListRoutes.root
          } else {
            dispatch(redirectToSta())
            return
          }
        }

        // Checking user declaration for trainees
        if (!content.isDeclarationConfirmed) {
          LoggerUtils.info("User declaration didn't confirmed")
          dispatch(getUserDeclaration())
          return
        }

        // Checking is Ocean Platform Enabled
        if (!login.isOceanEnabled) {
          LoggerUtils.info('OCEAN Platform disabled')
          dispatch(redirectToSta())
          return
        }

        // Checking we should use Legacy client
        if (login.shouldUseLegacyClient) {
          dispatch(redirectToSta({ pluginName: 'TaskStatusPlugin', moduleName: null }))
          return
        }

        // Checking previous opened URL
        if (returnUrl) {
          dispatch(push(returnUrl))
          return
        }

        dispatch(push(redirectRoute || homeRoutes.root))
      } else {
        dispatch(logoutUser())
        dispatch(userAuthenticated({ error: account.error }))
        return
      }
    }
    dispatch(userAuthenticated(response))
  }

export const authenticateUser = (formData: AuthUserParams) => {
  UserUtils.storeUserLoginParams({ ...formData, loginType: LoginType.Regular })

  return createApiAction({
    apiCalls: {
      login: () => login(formData),
    },
    payloadAction: userAuthenticated,
    nextAction: handleUserAuthentification,
  })
}

export const forgotPasswordRequest = (formData: ForgotPasswordParams) => {
  return createApiAction({
    apiCalls: {
      forgot: () => forgotPassword(formData),
    },
    payloadAction: userForgotPasswordRequested,
  })
}

export const setupPasswordRequest = (
  formData: SetupPasswordParams,
  emailLinkParams: SetupPasswordEmailLinkParams
) => {
  return createApiAction({
    apiCalls: {
      setup: () => setupPassword(formData, emailLinkParams),
    },
    payloadAction: userSetupPasswordRequested,
  })
}

export const changePasswordRequest = (
  formData: ChangePasswordParams,
  authUserParams: AuthUserParams
) => {
  return createApiAction({
    apiCalls: {
      setup: () => changePassword(formData, authUserParams),
    },
    payloadAction: userChangePasswordRequested,
  })
}

export const getUserAccount = () => {
  return createApiAction({
    apiCalls: {
      account: getAccount,
    },
    payloadAction: getAccountRequested,
  })
}

export const registerVessel = (formData: RegisterParams) => {
  return createApiAction({
    apiCalls: {
      registration: () => registerInstallation(formData),
    },
    payloadAction: registrationRequested,
  })
}
